import React, { useEffect, useRef } from 'react';

const W = 640, H = 360;

// Chroma-keys the green-screen D-ID stream onto a background image, client-side.
// The <video> stays full-size and visible (occluded by the canvas) so Chrome keeps decoding it in real time.
export const LiveAvatar = ({ avatar, className = '' }) => {
  const videoElRef = useRef(null);
  const canvasRef = useRef(null);
  const workRef = useRef(null);
  const bgRef = useRef(null);

  const setVideoEl = (el) => {
    videoElRef.current = el;
    avatar.videoRef(el);
  };

  useEffect(() => {
    if (!avatar.presenterImage) return;
    const img = new Image();
    img.onload = () => { bgRef.current = img; };
    img.src = avatar.presenterImage;
  }, [avatar.presenterImage]);

  useEffect(() => {
    if (!avatar.isLive) return undefined;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!workRef.current) workRef.current = document.createElement('canvas');
    const work = workRef.current;
    work.width = W; work.height = H;
    const wctx = work.getContext('2d', { willReadFrequently: true });
    let handle;
    let cancelled = false;

    const paint = () => {
      const v = videoElRef.current;
      if (v && v.readyState >= 2) {
        wctx.drawImage(v, 0, 0, W, H);
        const frame = wctx.getImageData(0, 0, W, H);
        const d = frame.data;
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i], g = d[i + 1], b = d[i + 2];
          const key = g - Math.max(r, b);
          if (key > 60) {
            d[i + 3] = 0;
          } else if (key > 20) {
            d[i + 3] = Math.round(255 * (1 - (key - 20) / 40));
            d[i + 1] = Math.max(r, b);
          } else if (g > r && g > b) {
            d[i + 1] = Math.max(r, b) + Math.min(g - Math.max(r, b), 8);
          }
        }
        wctx.putImageData(frame, 0, 0);
        if (bgRef.current) ctx.drawImage(bgRef.current, 0, 0, W, H); else ctx.clearRect(0, 0, W, H);
        ctx.drawImage(work, 0, 0);
      }
    };

    const schedule = () => {
      if (cancelled) return;
      const v = videoElRef.current;
      if (v && typeof v.requestVideoFrameCallback === 'function') {
        handle = v.requestVideoFrameCallback(() => { paint(); schedule(); });
      } else {
        handle = requestAnimationFrame(() => { paint(); schedule(); });
      }
    };
    schedule();
    return () => {
      cancelled = true;
      const v = videoElRef.current;
      if (v && typeof v.cancelVideoFrameCallback === 'function') { try { v.cancelVideoFrameCallback(handle); } catch (e) { /* noop */ } }
      cancelAnimationFrame(handle);
    };
  }, [avatar.isLive]);

  return (
    <div
      className={`absolute inset-0 overflow-hidden transition-opacity duration-500 ${avatar.isLive ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${className}`}
      data-testid="alec-live-avatar"
    >
      {/* Real-time decoded source: full size, visible, but fully covered by the composited canvas */}
      <video ref={setVideoEl} autoPlay playsInline data-testid="alec-live-video" className="absolute inset-0 w-full h-full object-cover" />
      <canvas ref={canvasRef} width={W} height={H} className="absolute inset-0 w-full h-full object-cover" />
    </div>
  );
};
